import { useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Activity,
  Bot,
  ChevronDown,
  Database,
  FileText,
  HeartPulse,
  LockKeyhole,
  LogOut,
  Search,
  Send,
  Sparkles,
  Settings2
} from "lucide-react";
import { AuthRequiredError, askMedPathAgentStream, getAgentHealth, getAuthSession, login, logout } from "./api";
import type { AgentHealth, AgentResponse, ConversationTurn, KnowledgeBaseItem, ModelChoice, ModelOption } from "./types";

interface Message {
  id: string;
  role: "agent" | "user";
  text: string;
  result?: AgentResponse;
  status?: string;
  streaming?: boolean;
}

const FALLBACK_KNOWLEDGE_BASE: KnowledgeBaseItem[] = [
  {
    name: "Patient MedPath.pdf",
    description: "Healthcare route guide for Singapore triage, GP and specialist choices, and care pathways.",
    chunks: 1
  },
  {
    name: "List to MedPath_Apr2026.xlsx",
    description: "Specialist directory with MedPath doctor options by specialty, priority, institution, and source link.",
    chunks: 1
  },
  {
    name: "PRUShield source documents",
    description: "PRUShield brochures, benefit wording, premiums, riders, and related insurance references.",
    chunks: 1
  }
];

const FALLBACK_MODEL_OPTIONS: ModelOption[] = [
  {
    id: "gpt-5.4-mini",
    label: "gpt-5.4-mini",
    description: "Default OpenAI model from the legacy local prototype.",
    provider: "openai"
  },
  {
    id: "extractive",
    label: "Extractive only",
    description: "No generative model; answer from retrieved source notes.",
    provider: "extractive"
  }
];

function readStoredValue(key: string) {
  try {
    return window.localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function storeValue(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Local storage can be unavailable in private contexts; the current session still works.
  }
}

function MarkdownContent({ text }: { text: string }) {
  return (
    <div className="messageMarkdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ ...props }) => <a {...props} target="_blank" rel="noreferrer" />
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

function buildConversationHistory(messages: Message[]): ConversationTurn[] {
  return messages
    .filter((message) => message.id !== "welcome" && !message.streaming && message.text.trim())
    .slice(-8)
    .map((message) => ({
      role: message.role === "user" ? "user" : "assistant",
      content: message.text.trim().slice(0, 1800)
    }));
}

function MessageBubble({ message }: { message: Message }) {
  const isAgent = message.role === "agent";
  const hasText = Boolean(message.text.trim());

  return (
    <article className={`message ${message.role}`}>
      <div className="messageIcon" aria-hidden="true">
        {isAgent ? <Bot size={18} /> : <span>Q</span>}
      </div>
      <div className="messageBody" aria-live={isAgent ? "polite" : undefined}>
        {hasText ? <MarkdownContent text={message.text} /> : null}
        {!hasText && message.streaming ? <p className="streamStatus">{message.status || "Working..."}</p> : null}
        {message.streaming && hasText ? <span className="streamingCursor" aria-hidden="true" /> : null}
        {message.result?.doctors?.length ? (
          <div className="doctorGrid">
            {message.result.doctors.map((doctor) => (
              <a className="doctorCard" href={doctor.url} target="_blank" rel="noreferrer" key={doctor.name}>
                <strong>{doctor.name}</strong>
                <span>{doctor.position || doctor.specialty}</span>
                <small>{[doctor.institution, doctor.sector].filter(Boolean).join(" · ")}</small>
              </a>
            ))}
          </div>
        ) : null}
        {message.result?.references.length ? (
          <div className="referenceRow">
            {message.result.references.map((reference) => (
              <span key={reference}>{reference}</span>
            ))}
          </div>
        ) : null}
      </div>
    </article>
  );
}

function EvidencePanel({ result }: { result?: AgentResponse }) {
  const matches = result?.matches || [];

  return (
    <aside className="evidencePane">
      <div className="paneHeader">
        <FileText size={18} />
        <h2>Evidence</h2>
      </div>
      {matches.length ? (
        <div className="matchList">
          {matches.map((match) => (
            <figure className="matchItem" key={`${match.source}-${match.page}-${match.preview}`}>
              <figcaption>
                <strong>{match.source}</strong>
                <span>{match.page || match.title}</span>
              </figcaption>
              <blockquote>{match.preview}</blockquote>
            </figure>
          ))}
        </div>
      ) : (
        <div className="emptyState">
          <Search size={22} />
          <p>Source excerpts will appear here when a response uses retrieved knowledge.</p>
        </div>
      )}
    </aside>
  );
}

function LoginScreen({ onLogin }: { onLogin: (user: string) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function submitLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);
    try {
      const result = await login(username.trim(), password);
      onLogin(result.user);
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : "Login failed.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="loginShell">
      <section className="loginPanel">
        <div className="brandBlock">
          <div className="brandMark">
            <HeartPulse size={22} />
          </div>
          <div>
            <h1>MedPath</h1>
            <span>Knowledge Assistant</span>
          </div>
        </div>
        <div className="loginTitle">
          <LockKeyhole size={24} />
          <div>
            <h2>Sign in</h2>
            <p>Access is restricted to protect usage and Cloudflare AI quota.</p>
          </div>
        </div>
        <form className="loginForm" onSubmit={submitLogin}>
          <label>
            Username
            <input
              autoComplete="username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              required
            />
          </label>
          <label>
            Password
            <input
              autoComplete="current-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>
          {error ? <p className="loginError">{error}</p> : null}
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Checking..." : "Sign in"}
          </button>
        </form>
      </section>
    </main>
  );
}

function PersonalNotesPanel({
  notes,
  onNotesChange
}: {
  notes: string;
  onNotesChange: (notes: string) => void;
}) {
  return (
    <section className="personalNotesPanel" aria-label="Personal notes for the agent">
      <label className="notesEditor">
        <span>
          <Settings2 size={15} />
          Personal notes for the agent
        </span>
        <textarea
          value={notes}
          onChange={(event) => onNotesChange(event.target.value.slice(0, 2000))}
          placeholder="Preferences, user context, language style, plan details to keep in mind."
          rows={7}
        />
        <small>{notes.length}/2000</small>
      </label>
    </section>
  );
}

function KnowledgeBaseRail({
  health,
  notes,
  onNotesChange
}: {
  health: AgentHealth | null;
  notes: string;
  onNotesChange: (notes: string) => void;
}) {
  const sources = health?.knowledgeBase?.length ? health.knowledgeBase : FALLBACK_KNOWLEDGE_BASE;
  const totalChunks = health?.chunks ?? sources.reduce((sum, item) => sum + item.chunks, 0);

  return (
    <section className="leftRail" aria-label="Knowledge base">
      <div className="brandBlock">
        <div className="brandMark">
          <HeartPulse size={22} />
        </div>
        <div>
          <h1>MedPath</h1>
          <span>Knowledge Assistant</span>
        </div>
      </div>

      <PersonalNotesPanel notes={notes} onNotesChange={onNotesChange} />

      <details className="kbDetails">
        <summary className="kbSummary">
          <Database size={18} />
          <div>
            <h2>Knowledge base</h2>
            <span>{totalChunks} source chunks</span>
          </div>
          <ChevronDown className="kbChevron" size={16} />
        </summary>
        <p className="kbIntro">
          MedPath insurance and healthcare routing reference for PRUShield materials, Singapore care paths, premiums,
          and specialist options.
        </p>
        <div className="kbList">
          {sources.map((source) => (
            <article className="kbItem" key={source.name}>
              <strong>{source.name}</strong>
              <p>{source.description}</p>
              <span>{source.chunks} chunks</span>
            </article>
          ))}
        </div>
      </details>
    </section>
  );
}

function SettingsMenu({
  user,
  model,
  modelOptions,
  onModelChange,
  onSignOut
}: {
  user: string | null;
  model: ModelChoice;
  modelOptions: ModelOption[];
  onModelChange: (model: ModelChoice) => void;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const selectedOption = modelOptions.find((option) => option.id === model) || modelOptions[0];

  return (
    <div className="settingsMenu">
      <button
        className="settingsButton"
        type="button"
        aria-expanded={open}
        aria-label="Open settings"
        onClick={() => setOpen((current) => !current)}
      >
        <Settings2 size={15} />
        <span>Settings</span>
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      {open ? (
        <div className="settingsPopover" aria-label="Settings menu">
          <label className="modelPicker">
            <span>
              <Sparkles size={15} />
              Model
            </span>
            <select value={model} onChange={(event) => onModelChange(event.target.value as ModelChoice)}>
              {modelOptions.map((option) => (
                <option value={option.id} key={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
            <small>{selectedOption?.description}</small>
          </label>
          <button className="settingsLogout" type="button" onClick={onSignOut}>
            <LogOut size={15} />
            Sign out{user ? ` ${user}` : ""}
          </button>
        </div>
      ) : null}
    </div>
  );
}
export default function App() {
  const [auth, setAuth] = useState<{ status: "checking" | "guest" | "authed"; user: string | null }>({
    status: "checking",
    user: null
  });
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "welcome",
      role: "agent",
      text: "MedPath is ready. Ask about PRUShield, PRUExtra, panel rules, care routes, or doctor options."
    }
  ]);
  const [question, setQuestion] = useState("");
  const [isAsking, setIsAsking] = useState(false);
  const [health, setHealth] = useState<AgentHealth | null>(null);
  const [selectedModel, setSelectedModel] = useState<ModelChoice>(() => {
    const stored = readStoredValue("medpath.selectedModel");
    return stored || "gpt-5.4-mini";
  });
  const [personalNotes, setPersonalNotes] = useState(() => readStoredValue("medpath.personalNotes"));
  const scrollRef = useRef<HTMLDivElement>(null);

  const modelOptions = useMemo(
    () => (health?.modelOptions?.length ? health.modelOptions : FALLBACK_MODEL_OPTIONS),
    [health]
  );

  const latestResult = useMemo(
    () => [...messages].reverse().find((message) => message.result)?.result,
    [messages]
  );

  useEffect(() => {
    getAuthSession()
      .then((session) => {
        if (session.authenticated) {
          setAuth({ status: "authed", user: session.user });
          return getAgentHealth().then(setHealth).catch(() => setHealth(null));
        }
        setAuth({ status: "guest", user: null });
        return null;
      })
      .catch(() => setAuth({ status: "guest", user: null }));
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, isAsking]);

  useEffect(() => {
    storeValue("medpath.selectedModel", selectedModel);
  }, [selectedModel]);

  useEffect(() => {
    storeValue("medpath.personalNotes", personalNotes);
  }, [personalNotes]);

  useEffect(() => {
    if (!modelOptions.some((option) => option.id === selectedModel)) {
      setSelectedModel(modelOptions[0]?.id || "gpt-5.4-mini");
    }
  }, [modelOptions, selectedModel]);

  async function submitQuestion(nextQuestion = question) {
    const clean = nextQuestion.trim();
    if (!clean || isAsking) return;

    setQuestion("");
    const userMessage: Message = { id: crypto.randomUUID(), role: "user", text: clean };
    const agentMessageId = crypto.randomUUID();
    const agentMessage: Message = {
      id: agentMessageId,
      role: "agent",
      text: "",
      status: "Searching the knowledge base...",
      streaming: true
    };
    setMessages((current) => [...current, userMessage, agentMessage]);
    setIsAsking(true);

    try {
      const result = await askMedPathAgentStream(
        clean,
        {
          model: selectedModel,
          personalNotes,
          history: buildConversationHistory(messages)
        },
        {
          onStatus: (status) => {
            setMessages((current) =>
              current.map((message) => (message.id === agentMessageId ? { ...message, status } : message))
            );
          },
          onDelta: (text) => {
            setMessages((current) =>
              current.map((message) =>
                message.id === agentMessageId ? { ...message, text: `${message.text}${text}` } : message
              )
            );
          }
        }
      );
      setMessages((current) =>
        current.map((message) =>
          message.id === agentMessageId
            ? {
                ...message,
                text: result.answer,
                result,
                status: undefined,
                streaming: false
              }
            : message
        )
      );
    } catch (error) {
      if (error instanceof AuthRequiredError) {
        setAuth({ status: "guest", user: null });
      }
      setMessages((current) =>
        current.map((message) =>
          message.id === agentMessageId
            ? {
                ...message,
                text: error instanceof Error ? error.message : "The assistant could not complete the request.",
                status: undefined,
                streaming: false
              }
            : message
        )
      );
    } finally {
      setIsAsking(false);
    }
  }

  async function signOut() {
    await logout();
    setHealth(null);
    setAuth({ status: "guest", user: null });
  }

  function handleLogin(user: string) {
    setAuth({ status: "authed", user });
    getAgentHealth().then(setHealth).catch(() => setHealth(null));
  }

  if (auth.status === "checking") {
    return (
      <main className="loginShell">
        <section className="loginPanel compact">
          <div className="brandBlock">
            <div className="brandMark">
              <HeartPulse size={22} />
            </div>
            <div>
              <h1>MedPath</h1>
              <span>Checking session...</span>
            </div>
          </div>
        </section>
      </main>
    );
  }

  if (auth.status === "guest") {
    return <LoginScreen onLogin={handleLogin} />;
  }

  return (
    <main className="appShell">
      <KnowledgeBaseRail health={health} notes={personalNotes} onNotesChange={setPersonalNotes} />

      <section className="chatPane" aria-label="MedPath question and answer">
        <header className="chatHeader">
          <div>
            <span className="systemLabel">
              <Activity size={15} />
              {health?.ok ? "online" : "local"}
            </span>
            <h2>Ask MedPath</h2>
          </div>
          <SettingsMenu
            user={auth.user}
            model={selectedModel}
            modelOptions={modelOptions}
            onModelChange={setSelectedModel}
            onSignOut={signOut}
          />
        </header>

        <div className="messageList" ref={scrollRef}>
          {messages.map((message) => (
            <MessageBubble message={message} key={message.id} />
          ))}
        </div>

        <form
          className="composer"
          onSubmit={(event) => {
            event.preventDefault();
            submitQuestion();
          }}
        >
          <textarea
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault();
                submitQuestion();
              }
            }}
            placeholder="Ask MedPath..."
            rows={2}
          />
          <button type="submit" disabled={!question.trim() || isAsking} aria-label="Send question">
            <Send size={18} />
          </button>
        </form>
      </section>

      <EvidencePanel result={latestResult} />
    </main>
  );
}
